// Features.js
import { Link } from "react-router-dom";
import { useState, useEffect } from 'react';
import MarketingLayout from "./components/MarketingLayout.js";
import "./Features.css";

export default function Features() {

  // Separate state for each demo
  const [webProgress, setWebProgress] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [imageProgress, setImageProgress] = useState(0);
  const [typedCode, setTypedCode] = useState('');
  const [currentLineIndex, setCurrentLineIndex] = useState(0);

  const demos = [
    {
      id: 'web',
      icon: 'Web',
      title: 'Building Website',
      color: '#0f766e',
      steps: [
        { text: 'Creating component structure', duration: 3000, file: 'App.jsx', codeLines: [
          'import React from "react";',
          'import "./App.css";',
          '',
          'function App() {',
          '  return (',
          '    <div className="app">',
          '      <header className="header">',
          '        <h1>Zolaid</h1>',
          '      </header>',
          '    </div>',
          '  );',
          '}',
          '',
          'export default App;'
        ]},
        { text: 'Adding hero section', duration: 2500, file: 'App.jsx', codeLines: [
          'import React from "react";',
          'import "./App.css";',
          '',
          'function App() {',
          '  return (',
          '    <div className="app">',
          '      <header className="header">',
          '        <h1>Zolaid</h1>',
          '      </header>',
          '      <section className="hero">',
          '        <h2>Build with AI</h2>',
          '        <p>Create amazing things</p>',
          '      </section>',
          '    </div>',
          '  );',
          '}',
          '',
          'export default App;'
        ]},
        { text: 'Styling components', duration: 2800, file: 'App.css', codeLines: [
          '.app {',
          '  min-height: 100vh;',
          '  background: linear-gradient(',
          '    135deg, #0b1f33 0%, #0f766e 100%',
          '  );',
          '}',
          '',
          '.hero {',
          '  text-align: center;',
          '  padding: 4rem 2rem;',
          '  color: white;',
          '}',
          '',
          '.hero h2 {',
          '  font-size: 3rem;',
          '  margin-bottom: 1rem;',
          '}'
        ]},
        { text: 'Adding interactivity', duration: 3200, file: 'App.jsx', codeLines: [
          'import React, { useState } from "react";',
          'import "./App.css";',
          '',
          'function App() {',
          '  const [count, setCount] = useState(0);',
          '',
          '  return (',
          '    <div className="app">',
          '      <header className="header">',
          '        <h1>Zolaid</h1>',
          '      </header>',
          '      <section className="hero">',
          '        <h2>Build with AI</h2>',
          '        <p>Create amazing things</p>',
          '        <button onClick={() => setCount(count + 1)}>',
          '          Clicked {count} times',
          '        </button>',
          '      </section>',
          '    </div>',
          '  );',
          '}',
          '',
          'export default App;'
        ]},
        { text: 'Finalizing deployment', duration: 2000, file: 'package.json', codeLines: [
          '{',
          '  "name": "zolaid-backend",',
          '  "version": "1.0.0",',
          '  "scripts": {',
          '    "dev": "vite",',
          '    "build": "vite build",',
          '    "preview": "vite preview"',
          '  },',
          '  "dependencies": {',
          '    "react": "^18.2.0",',
          '    "react-dom": "^18.2.0"',
          '  },',
          '  "devDependencies": {',
          '    "vite": "^4.4.0"',
          '  }',
          '}'
        ]}
      ],
      preview: {
        type: 'code'
      }
    },
    {
      id: 'video',
      icon: 'Video',
      title: 'Editing Video',
      color: '#0b1f33',
      steps: [
        { text: 'Analyzing video content', duration: 2500, effect: 'analyzing' },
        { text: 'Trimming and cutting clips', duration: 2800, effect: 'cutting' },
        { text: 'Adding transitions', duration: 2400, effect: 'transitions' },
        { text: 'Color grading applied', duration: 2600, effect: 'color' },
        { text: 'Rendering HD video', duration: 2200, effect: 'rendering' }
      ],
      preview: {
        type: 'video',
        tracks: [
          { name: 'Video Track 1', clips: [
            { name: 'Intro.mp4', start: 0, duration: 3, color: '#0f766e' },
            { name: 'Main_Scene.mp4', start: 3, duration: 5, color: '#0b1f33' },
            { name: 'Outro.mp4', start: 8, duration: 2, color: '#0d9488' }
          ]},
          { name: 'Audio Track', clips: [
            { name: 'Background_Music.mp3', start: 0, duration: 10, color: '#115e59' }
          ]},
          { name: 'Text Overlay', clips: [
            { name: 'Title_Animation', start: 1, duration: 2, color: '#134e4a' }
          ]}
        ]
      }
    },
    {
      id: 'image',
      icon: 'Image',
      title: 'Editing Image',
      color: '#0d9488',
      steps: [
        { text: 'Loading image assets', duration: 2000, tool: 'load' },
        { text: 'AI background removal', duration: 2800, tool: 'remove-bg' },
        { text: 'Adjusting brightness +20%', duration: 2200, tool: 'brightness' },
        { text: 'Applying blur effect', duration: 2400, tool: 'blur' },
        { text: 'Exporting PNG (1920x1080)', duration: 2000, tool: 'export' }
      ],
      preview: {
        type: 'image',
        tools: [
          { name: 'Move', icon: 'Move', category: 'basic' },
          { name: 'Select', icon: 'Select', category: 'basic' },
          { name: 'Brush', icon: 'Brush', category: 'paint' },
          { name: 'Eraser', icon: 'Eraser', category: 'paint' },
          { name: 'Text', icon: 'Text', category: 'text' },
          { name: 'Shape', icon: 'Shape', category: 'shape' },
          { name: 'Crop', icon: 'Crop', category: 'edit' },
          { name: 'Filter', icon: 'Filter', category: 'edit' }
        ],
        layers: [
          { name: 'Background', visible: true, opacity: 100 },
          { name: 'Subject Layer', visible: true, opacity: 100 },
          { name: 'Color Adjustment', visible: true, opacity: 80 },
          { name: 'Blur Effect', visible: true, opacity: 60 }
        ]
      }
    }
  ];

  // Web demo animation
  useEffect(() => {
    const totalSteps = demos[0].steps.length;

    if (webProgress >= totalSteps) {
      const timer = setTimeout(() => setWebProgress(0), 2000);
      return () => clearTimeout(timer);
    }

    const currentStep = demos[0].steps[webProgress];
    const timer = setTimeout(() => {
      setWebProgress(prev => prev + 1);
    }, currentStep.duration);

    return () => clearTimeout(timer);
  }, [webProgress]);

  // Video demo animation
  useEffect(() => {
    const totalSteps = demos[1].steps.length;

    if (videoProgress >= totalSteps) {
      const timer = setTimeout(() => setVideoProgress(0), 2000);
      return () => clearTimeout(timer);
    }

    const currentStep = demos[1].steps[videoProgress];
    const timer = setTimeout(() => {
      setVideoProgress(prev => prev + 1);
    }, currentStep.duration);

    return () => clearTimeout(timer);
  }, [videoProgress]);

  // Image demo animation
  useEffect(() => {
    const totalSteps = demos[2].steps.length;

    if (imageProgress >= totalSteps) {
      const timer = setTimeout(() => setImageProgress(0), 2000);
      return () => clearTimeout(timer);
    }

    const currentStep = demos[2].steps[imageProgress];
    const timer = setTimeout(() => {
      setImageProgress(prev => prev + 1);
    }, currentStep.duration);

    return () => clearTimeout(timer);
  }, [imageProgress]);

  // Typing animation for web demo code
  useEffect(() => {
    if (!demos[0].steps[webProgress]) return;

    const currentStep = demos[0].steps[webProgress];
    if (!currentStep.codeLines) return;

    const fullCode = currentStep.codeLines.join('\n');
    let charIndex = 0;

    setTypedCode('');
    setCurrentLineIndex(0);

    const typingInterval = setInterval(() => {
      if (charIndex < fullCode.length) {
        setTypedCode(fullCode.substring(0, charIndex + 1));
        charIndex++;

        // Update current line index
        const linesTyped = fullCode.substring(0, charIndex).split('\n').length - 1;
        setCurrentLineIndex(linesTyped);
      } else {
        clearInterval(typingInterval);
      }
    }, 30); // Type speed: 30ms per character

    return () => clearInterval(typingInterval);
  }, [webProgress]);

  return (
    <MarketingLayout flush>
    <div className="features-page">
      {/* Hero Section */}
      <section className="features-hero-modern">
        <div className="container">
          <div className="hero-content">
            <p className="features-brand">Zolaid</p>
            <h1 className="hero-title">Built to make you extraordinarily productive</h1>
            <p className="hero-subtitle">Agents, autocomplete, and integrations in one AI workspace.</p>

            <div className="hero-actions">
              <Link to="/chatbot" className="btn-primary">
                Get Started
                <span className="btn-icon">→</span>
              </Link>
              <Link to="/pricing" className="btn-secondary">View Pricing</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Web Creation Demo Section */}
      <section className="fullscreen-demo-section web-demo">
        <div className="fullscreen-demo-container">
          <div className="fullscreen-demo-header">
            <h2 className="fullscreen-demo-title">AI-Powered Web Development</h2>
            <p className="fullscreen-demo-subtitle">Watch Zolaid AI code a complete website from scratch in seconds</p>
          </div>

          <div className="fullscreen-demo-window">
            <div className="fullscreen-demo-window-header">
              <div className="window-controls">
                <span className="control-dot red"></span>
                <span className="control-dot yellow"></span>
                <span className="control-dot green"></span>
              </div>
              <div className="window-title">{demos[0].title}</div>
              <div className="window-spacer"></div>
            </div>

            <div className="fullscreen-demo-content">
              {/* Left Side: Progress Steps */}
              <div className="fullscreen-demo-left-panel">
                <div className="demo-steps-container">
                  {demos[0].steps.map((step, index) => (
                    <div
                      key={index}
                      className={`demo-step ${
                        index < webProgress ? 'completed' :
                        index === webProgress ? 'active' : 'pending'
                      }`}
                    >
                      <div className="step-indicator">
                        {index < webProgress ? (
                          <span className="step-check">✓</span>
                        ) : index === webProgress ? (
                          <span className="step-spinner">⟳</span>
                        ) : (
                          <span className="step-dot">○</span>
                        )}
                      </div>
                      <div className="step-text">{step.text}</div>
                      {index === webProgress && (
                        <div className="step-progress-bar">
                          <div className="step-progress-fill" style={{ backgroundColor: demos[0].color }}></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side: IDE Preview */}
              <div className="fullscreen-demo-right-panel">
                <div className="demo-ide-container">
                  <div className="ide-sidebar">
                    <div className="sidebar-header">
                      <span className="sidebar-title">📁 PROJECT</span>
                    </div>
                    <div className="file-tree">
                      <div className="file-tree-item folder open">
                        <span className="file-icon">📂</span>
                        <span className="file-name">src</span>
                      </div>
                      <div className={`file-tree-item file nested ${demos[0].steps[webProgress]?.file === 'App.jsx' ? 'active' : ''}`}>
                        <span className="file-icon">⚛️</span>
                        <span className="file-name">App.jsx</span>
                      </div>
                      <div className={`file-tree-item file nested ${demos[0].steps[webProgress]?.file === 'App.css' ? 'active' : ''}`}>
                        <span className="file-icon">🎨</span>
                        <span className="file-name">App.css</span>
                      </div>
                      <div className="file-tree-item file">
                        <span className="file-icon">📦</span>
                        <span className="file-name">package.json</span>
                      </div>
                      <div className="file-tree-item file">
                        <span className="file-icon">📝</span>
                        <span className="file-name">README.md</span>
                      </div>
                    </div>
                    <div className="sidebar-footer">
                      <div className="ai-badge">
                        <span className="ai-icon">✨</span>
                        <span className="ai-text">Zolaid AI</span>
                      </div>
                    </div>
                  </div>

                  <div className="ide-editor">
                    <div className="editor-tabs">
                      {demos[0].steps.slice(0, webProgress + 1).map((step, idx) => (
                        <div
                          key={idx}
                          className={`editor-tab ${step.file === demos[0].steps[webProgress]?.file ? 'active' : ''}`}
                        >
                          <span className="tab-icon">
                            {step.file?.endsWith('.jsx') ? '⚛️' :
                             step.file?.endsWith('.css') ? '🎨' :
                             step.file?.endsWith('.json') ? '📦' : '📄'}
                          </span>
                          <span className="tab-name">{step.file}</span>
                        </div>
                      ))}
                    </div>

                    <div className="editor-content">
                      <div className="line-numbers">
                        {typedCode.split('\n').map((_, idx) => (
                          <div
                            key={idx}
                            className={`line-number ${idx === currentLineIndex ? 'active' : ''}`}
                          >
                            {idx + 1}
                          </div>
                        ))}
                      </div>

                      <div className="code-area">
                        <pre className="code-text">
                          <code>{typedCode}</code>
                          <span className="code-cursor">|</span>
                        </pre>
                      </div>
                    </div>

                    <div className="editor-statusbar">
                      <div className="status-left">
                        <span className="status-item">
                          <span className="status-icon">✓</span>
                          <span className="status-text">No Issues</span>
                        </span>
                        <span className="status-item">
                          <span className="status-icon">📝</span>
                          <span className="status-text">Ln {currentLineIndex + 1}, Col {typedCode.split('\n')[currentLineIndex]?.length || 0}</span>
                        </span>
                      </div>
                      <div className="status-right">
                        <span className="status-item">React</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Editing Demo Section */}
      <section className="fullscreen-demo-section video-demo">
        <div className="fullscreen-demo-container">
          <div className="fullscreen-demo-header">
            <h2 className="fullscreen-demo-title">AI-Powered Video Editing</h2>
            <p className="fullscreen-demo-subtitle">Watch Zolaid AI edit and enhance videos with professional effects</p>
          </div>

          <div className="fullscreen-demo-window">
            <div className="fullscreen-demo-window-header">
              <div className="window-controls">
                <span className="control-dot red"></span>
                <span className="control-dot yellow"></span>
                <span className="control-dot green"></span>
              </div>
              <div className="window-title">{demos[1].title}</div>
              <div className="window-spacer"></div>
            </div>

            <div className="fullscreen-demo-content">
              {/* Left Side: Progress Steps */}
              <div className="fullscreen-demo-left-panel">
                <div className="demo-steps-container">
                  {demos[1].steps.map((step, index) => (
                    <div
                      key={index}
                      className={`demo-step ${
                        index < videoProgress ? 'completed' :
                        index === videoProgress ? 'active' : 'pending'
                      }`}
                    >
                      <div className="step-indicator">
                        {index < videoProgress ? (
                          <span className="step-check">✓</span>
                        ) : index === videoProgress ? (
                          <span className="step-spinner">⟳</span>
                        ) : (
                          <span className="step-dot">○</span>
                        )}
                      </div>
                      <div className="step-text">{step.text}</div>
                      {index === videoProgress && (
                        <div className="step-progress-bar">
                          <div className="step-progress-fill" style={{ backgroundColor: demos[1].color }}></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side: Video Editor Preview */}
              <div className="fullscreen-demo-right-panel">
                <div className="demo-video-editor">
                  <div className="video-preview-window">
                    <div className="video-screen">
                      <div className="video-content">
                        <div className="play-button-large">▶</div>
                        <div className="video-timecode">00:00:{String(videoProgress * 2).padStart(2, '0')}</div>
                      </div>
                    </div>
                    <div className="video-preview-controls">
                      <button className="control-btn">⏮</button>
                      <button className="control-btn play">▶</button>
                      <button className="control-btn">⏭</button>
                      <div className="timeline-scrubber">
                        <div
                          className="scrubber-progress"
                          style={{
                            width: `${(videoProgress / demos[1].steps.length) * 100}%`,
                            backgroundColor: demos[1].color
                          }}
                        ></div>
                      </div>
                      <span className="duration-label">0:10</span>
                    </div>
                  </div>

                  <div className="video-timeline-editor">
                    <div className="timeline-header">
                      <span className="timeline-title">Timeline</span>
                      <div className="timeline-tools">
                        <button className="tool-btn" title="Cut">Cut</button>
                        <button className="tool-btn" title="Transition">Fx</button>
                        <button className="tool-btn" title="Effects">Glow</button>
                      </div>
                    </div>
                    <div className="timeline-tracks">
                      {demos[1].preview.tracks.map((track, trackIdx) => (
                        <div key={trackIdx} className="timeline-track">
                          <div className="track-label">{track.name}</div>
                          <div className="track-content">
                            {track.clips.map((clip, clipIdx) => (
                              <div
                                key={clipIdx}
                                className={`timeline-clip ${videoProgress > clipIdx ? 'active' : ''}`}
                                style={{
                                  left: `${(clip.start / 10) * 100}%`,
                                  width: `${(clip.duration / 10) * 100}%`,
                                  backgroundColor: clip.color
                                }}
                              >
                                <span className="clip-name-small">{clip.name}</span>
                                {videoProgress === clipIdx + 1 && (
                                  <div className="clip-processing">
                                    <span className="processing-indicator">⟳</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="video-effects-panel">
                    <div className="effects-header">Effects</div>
                    <div className="effect-item">
                      <span className="effect-icon">CG</span>
                      <span className="effect-name">Color Grade</span>
                      <div className={`effect-status ${videoProgress >= 3 ? 'applied' : ''}`}>
                        {videoProgress >= 3 ? 'Applied' : 'Ready'}
                      </div>
                    </div>
                    <div className="effect-item">
                      <span className="effect-icon">Fx</span>
                      <span className="effect-name">Blur Transition</span>
                      <div className={`effect-status ${videoProgress >= 2 ? 'applied' : ''}`}>
                        {videoProgress >= 2 ? 'Applied' : 'Ready'}
                      </div>
                    </div>
                    <div className="effect-item">
                      <span className="effect-icon">Au</span>
                      <span className="effect-name">Audio Fade</span>
                      <div className={`effect-status ${videoProgress >= 1 ? 'applied' : ''}`}>
                        {videoProgress >= 1 ? 'Applied' : 'Ready'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Editing Demo Section */}
      <section className="fullscreen-demo-section image-demo">
        <div className="fullscreen-demo-container">
          <div className="fullscreen-demo-header">
            <h2 className="fullscreen-demo-title">AI-Powered Image Editing</h2>
            <p className="fullscreen-demo-subtitle">Watch Zolaid AI transform images with advanced editing tools</p>
          </div>

          <div className="fullscreen-demo-window">
            <div className="fullscreen-demo-window-header">
              <div className="window-controls">
                <span className="control-dot red"></span>
                <span className="control-dot yellow"></span>
                <span className="control-dot green"></span>
              </div>
              <div className="window-title">{demos[2].title}</div>
              <div className="window-spacer"></div>
            </div>

            <div className="fullscreen-demo-content">
              {/* Left Side: Progress Steps */}
              <div className="fullscreen-demo-left-panel">
                <div className="demo-steps-container">
                  {demos[2].steps.map((step, index) => (
                    <div
                      key={index}
                      className={`demo-step ${
                        index < imageProgress ? 'completed' :
                        index === imageProgress ? 'active' : 'pending'
                      }`}
                    >
                      <div className="step-indicator">
                        {index < imageProgress ? (
                          <span className="step-check">✓</span>
                        ) : index === imageProgress ? (
                          <span className="step-spinner">⟳</span>
                        ) : (
                          <span className="step-dot">○</span>
                        )}
                      </div>
                      <div className="step-text">{step.text}</div>
                      {index === imageProgress && (
                        <div className="step-progress-bar">
                          <div className="step-progress-fill" style={{ backgroundColor: demos[2].color }}></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side: Image Editor Preview */}
              <div className="fullscreen-demo-right-panel">
                <div className="demo-image-editor">
                  <div className="image-tools-panel">
                    <div className="tools-header">Tools</div>
                    <div className="tools-grid">
                      {demos[2].preview.tools.map((tool, idx) => (
                        <div
                          key={idx}
                          className={`tool-item ${imageProgress === idx ? 'active' : ''}`}
                          title={tool.name}
                        >
                          <span className="tool-icon-img">{tool.icon}</span>
                        </div>
                      ))}
                    </div>
                    <div className="tools-section">
                      <div className="section-title">Properties</div>
                      <div className="property-item">
                        <label>Brightness</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={Math.min(100, imageProgress * 20)}
                            readOnly
                            className="property-slider"
                          />
                          <span className="slider-value">{Math.min(100, imageProgress * 20)}%</span>
                        </div>
                      </div>
                      <div className="property-item">
                        <label>Contrast</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={Math.min(100, imageProgress * 15)}
                            readOnly
                            className="property-slider"
                          />
                          <span className="slider-value">{Math.min(100, imageProgress * 15)}%</span>
                        </div>
                      </div>
                      <div className="property-item">
                        <label>Blur</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={Math.min(100, imageProgress * 10)}
                            readOnly
                            className="property-slider"
                          />
                          <span className="slider-value">{Math.min(100, imageProgress * 10)}px</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="image-canvas-area">
                    <div className="canvas-toolbar">
                      <div className="toolbar-left">
                        <button className="toolbar-btn">↶</button>
                        <button className="toolbar-btn">↷</button>
                        <span className="toolbar-divider">|</span>
                        <button className="toolbar-btn">✂️</button>
                        <button className="toolbar-btn">📐</button>
                      </div>
                      <div className="toolbar-center">
                        <span className="zoom-label">{100 + imageProgress * 10}%</span>
                      </div>
                      <div className="toolbar-right">
                        <button className="toolbar-btn">⊞</button>
                        <button className="toolbar-btn">⊡</button>
                      </div>
                    </div>
                    <div className="image-canvas-main">
                      <div className="canvas-grid"></div>
                      <div
                        className="canvas-image"
                        style={{
                          filter: `brightness(${100 + imageProgress * 20}%) contrast(${100 + imageProgress * 15}%) blur(${Math.min(5, imageProgress)}px)`,
                          transform: `scale(${1 + imageProgress * 0.05})`
                        }}
                      >
                        <div className="sample-image">
                          <div className="image-subject"></div>
                        </div>
                      </div>
                      {imageProgress > 0 && (
                        <div className="canvas-overlay">
                          <div className="processing-badge">
                            <span className="badge-icon">✨</span>
                            <span className="badge-text">AI Processing...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="image-layers-panel">
                    <div className="layers-panel-header">
                      <span className="layers-title">Layers</span>
                      <div className="layer-actions">
                        <button className="layer-action-btn" title="Add Layer">+</button>
                        <button className="layer-action-btn" title="Delete Layer">🗑️</button>
                      </div>
                    </div>
                    <div className="layers-list">
                      {demos[2].preview.layers.map((layer, idx) => (
                        <div
                          key={idx}
                          className={`layer-item-img ${imageProgress > idx ? 'active' : ''}`}
                        >
                          <div className="layer-visibility">
                            {layer.visible ? '👁️' : '👁️‍🗨️'}
                          </div>
                          <div className="layer-thumbnail">
                            <div className="thumbnail-preview"></div>
                          </div>
                          <div className="layer-info">
                            <div className="layer-name-img">{layer.name}</div>
                            <div className="layer-opacity-value">
                              {imageProgress > idx ? Math.max(0, layer.opacity - (imageProgress - idx) * 10) : layer.opacity}%
                            </div>
                          </div>
                          {imageProgress === idx + 1 && (
                            <div className="layer-processing">⟳</div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="layers-footer">
                      <div className="blend-mode">
                        <label>Blend:</label>
                        <select className="blend-select">
                          <option>Normal</option>
                          <option>Multiply</option>
                          <option>Screen</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to build with AI?</h2>
            <p className="cta-subtitle">Type a goal. Zolaid does the work on the computer, including websites, slides, and video.</p>
            <div className="cta-actions">
              <Link to="/chatbot" className="btn-primary-large">
                Get Started Free
                <span className="btn-icon">→</span>
              </Link>
              <Link to="/pricing" className="btn-secondary-large">View Pricing</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </MarketingLayout>
  );
}
